/**
 * index.tsx
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */

import * as React from 'react';
import CacheBuster from 'react-cache-buster';
import * as ReactDOM from 'react-dom';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClientProvider } from 'react-query';
import { Provider } from 'react-redux';
import { datadogRum } from '@datadog/browser-rum';
import { FeatureFlagProvider, ThemeProvider } from 'contexts';
import { persistStore } from 'redux-persist';
import { PersistGate } from 'redux-persist/integration/react';
import reportWebVitals from 'reportWebVitals';

// Import root app
import { App } from 'app';
import ErrorBoundary from 'app/components/ErrorBoundary';
import ToastContainer from 'app/components/ToastContainer';
import { queryClient } from 'reactQuery';
import store from 'store/configureStore';
import CustomRouter from 'utils/CustomRouter';
import history from 'utils/history';

import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';
// Initialize languages
import './locales/i18n';

import packageInfo from '../package.json';

import makeMockServer from './api/mock';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';

import './index.css';
// Use consistent styling
import 'sanitize.css/sanitize.css';

const mockEnabled = process.env.REACT_APP_MOCK_ENABLED;
const environment = process.env.NODE_ENV;
const cacheBusterEnabled =
  process.env.REACT_APP_CACHE_BUSTER_ENABLED === 'true';
const persistor = persistStore(store);
const MOUNT_NODE = document.getElementById('root') as HTMLElement;

if (process.env.NODE_ENV === 'development') {
  makeMockServer({ environment, mockEnabled });
}

datadogRum.init({
  applicationId: process.env.REACT_APP_DATADOG_APPLICATION_ID ?? '',
  clientToken: process.env.REACT_APP_DATADOG_CLIENT_TOKEN ?? '',
  site: 'us5.datadoghq.com',
  service: 'customer-platform-2w',
  env: process.env.REACT_APP_DATADOG_ENVIRONMENT ?? '',
  version: packageInfo.version,
  sessionSampleRate: 100,
  premiumSampleRate: 100,
  trackUserInteractions: true,
  defaultPrivacyLevel: 'mask-user-input',
  allowedTracingUrls: [
    url =>
      url.startsWith(process.env.REACT_APP_DATADOG_ALLOWED_TRACING_URL ?? ''),
  ],
});
datadogRum.startSessionReplayRecording();

ReactDOM.render(
  <React.StrictMode>
    <CustomRouter history={history}>
      <CacheBuster
        currentVersion={packageInfo.version}
        isEnabled={cacheBusterEnabled}
      >
        <QueryClientProvider client={queryClient}>
          <Provider store={store}>
            <PersistGate loading={null} persistor={persistor}>
              <HelmetProvider>
                <ThemeProvider>
                  <FeatureFlagProvider>
                    <ErrorBoundary>
                      <App />
                    </ErrorBoundary>
                    <ToastContainer />
                  </FeatureFlagProvider>
                </ThemeProvider>
              </HelmetProvider>
            </PersistGate>
          </Provider>
        </QueryClientProvider>
      </CacheBuster>
    </CustomRouter>
  </React.StrictMode>,
  MOUNT_NODE,
);

// Hot reloadable translation json files
if (module.hot) {
  module.hot.accept(['./locales/i18n'], () => {
    // No need to render the App again because i18next works with the hooks
  });
}
serviceWorkerRegistration.register();
// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
